import { Link } from "react-router-dom";
import { formatDayDe } from "@vocifuchs/shared";
import { common } from "../copy/common.js";
import { lists as listCopy } from "../copy/lists.js";
import { ListProgress, isExamAhead } from "./ListProgress.js";
import { ButtonLink, Card, EmptyState } from "./ui.js";

export interface ListSummary {
  id: string;
  title: string;
  lang_b: "en" | "fr";
  due_date: string | null;
  item_count: number;
  updated_at: number;
  stars: 0 | 1 | 2 | 3;
  ready: number;
  progress: number;
  is_owner: boolean;
  /** The child's own mark (issue #99). */
  mark: "current" | "archived" | null;
  current_at: number | null;
}

const LANG = common.lang;

/** A group of list cards: on Start, in «Aktuell», in the archive. */
export function ListGrid({
  kind,
  title,
  lists,
  seenAt,
  emptyTitle,
  emptyText,
}: {
  /** «mine» offers a new list; the other groups only show what they hold. */
  kind: "mine" | "class" | "current" | "archived";
  /** None in the archive, where the page heading says it all. */
  title?: string;
  lists: ListSummary[] | null;
  seenAt?: number;
  emptyTitle?: string;
  emptyText?: string;
}) {
  return (
    <section>
      <div className="mb-3 flex items-end justify-between gap-4 empty:hidden">
        {title ? <h2 className="text-xl sm:text-2xl">{title}</h2> : null}
        {kind === "mine" ? (
          <Link
            to="/lists/new"
            className="font-semibold text-fox-ink underline-offset-4 hover:underline"
          >
            {common.nav.newList}
          </Link>
        ) : null}
      </div>

      {lists && lists.length === 0 && emptyTitle ? (
        <EmptyState
          title={emptyTitle}
          action={
            kind === "mine" ? (
              <ButtonLink to="/lists/new" block={false} className="mt-2">
                {listCopy.home.firstList}
              </ButtonLink>
            ) : undefined
          }
        >
          {emptyText}
        </EmptyState>
      ) : (
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {(lists ?? []).map((list) => {
            // «neu» is for what someone else shared, never for the child's own
            // edit, also when both sit together in «Aktuell».
            const isNew = seenAt !== undefined && !list.is_owner && list.updated_at > seenAt;
            // Three stars fit next to the title; «12 von 15 sitzen für Freitag»
            // does not, and on a tablet it squeezed the title into three lines.
            // The long form gets its own row under the card's details.
            const beforeExam = isExamAhead(list.due_date);
            const progress = (
              <ListProgress
                stars={list.stars}
                ready={list.ready}
                progress={list.progress}
                total={list.item_count}
                dueDate={list.due_date}
              />
            );
            return (
              <Card as="li" key={list.id} className="transition-shadow hover:shadow-lg">
                <Link
                  to={`/lists/${list.id}`}
                  className="flex min-h-20 flex-col justify-between gap-3 rounded-3xl p-4 sm:p-5"
                >
                  <span className="flex items-start justify-between gap-3">
                    <span className="font-display text-lg leading-snug font-semibold">
                      {list.title}
                    </span>
                    {beforeExam ? null : progress}
                  </span>
                  <span className="text-sm text-ink-soft">
                    {isNew ? <span className="mr-2 font-semibold text-moss">{listCopy.home.newBadge}</span> : null}
                    {common.words(list.item_count)} · {LANG[list.lang_b]}
                    {list.due_date && !beforeExam
                      ? ` · ${common.exam(formatDayDe(list.due_date))}`
                      : ""}
                  </span>
                  {beforeExam ? <span className="-ml-1 flex">{progress}</span> : null}
                </Link>
              </Card>
            );
          })}
          {lists === null
            ? Array.from({ length: 3 }, (_, i) => (
                <li
                  key={i}
                  className="min-h-20 animate-pulse rounded-3xl bg-paper-2"
                  aria-hidden
                />
              ))
            : null}
        </ul>
      )}
      {lists === null ? <p className="sr-only">{common.loading}</p> : null}
    </section>
  );
}
